import React, { useEffect } from "react";
import { request } from "components/hooks/useQuery";
import { GetStaticPropsContext } from "next";
import { PageSlug } from "types/base";
import Container from "components/core/Container";
import cn from "classnames";
import Intro from "components/blog/blogPost/Intro";
import tocbot from "tocbot";
import Share from "components/blog/blogPost/Share";
import { useRouter } from "next/router";
import { createSubscription } from "components/hooks/usePreview";
import { withAppLayout } from "components/core/withAppLayout";
import useTrans from "components/hooks/useTrans";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "./../../../next-i18next.config";
import { getAllCustomerStorySlugs, getCustomerStoryBySlug } from "queries/Customers";
import BlockMapper from "blocks/BlockMapper";
import { ICustomerStory } from "pages/customers/index";
import CtaBlock from "blocks/cta/CtaBlock";

export async function getStaticPaths({ locales }: any) {
    const { allCustomerStories } = await request({
        query: getAllCustomerStorySlugs
    });

    const paths = allCustomerStories.map(({ _allSlugLocales }: any): any[] => {
        return _allSlugLocales.filter((customerStory: any) => {
            return locales.includes(customerStory.locale);
        }).map((customerStory: any) => {
            return {
                params: { slug: customerStory.value }, locale: customerStory.locale
            };
        }).flat();
    }).flat();

    return {
        paths: paths,
        fallback: false
    };
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const params = context.params as PageSlug;

    const query = {
        query: getCustomerStoryBySlug(params.slug, context.locale),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    const data = await createSubscription(context, query);

    if (!data?.initialData?.customerStory) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            data: data,
            seo: data.initialData.customerStory.seo,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        },
    };
}

type Props = {
    customerStory: ICustomerStory
};

function CustomerStory(props: Props) {
    const { content } = props.customerStory;
    return (
        <>
            {content.map((block: any) => Object.keys(block).length > 0 && (
                <BlockMapper key={block.id} {...block} />
            ))}
        </>
    );
}

export default withAppLayout(CustomerStory);
